/**
 * useAdmin Hook
 * React hook to check and manage admin status
 */

import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { isCurrentUserAdmin } from '../utils/adminUtils';

interface UseAdminResult {
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to check if current user is an admin
 * @returns UseAdminResult
 */
export function useAdmin(): UseAdminResult {
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get profile from Redux store
  const profile = useSelector((state: RootState) => state.auth?.profile);
  const isAuthenticated = useSelector((state: RootState) => state.auth?.isAuthenticated);

  const checkAdminStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!isAuthenticated || !profile) {
        setIsAdmin(false);
        return;
      }

      // First check from profile in store (faster)
      if (profile.is_admin !== undefined) {
        setIsAdmin(profile.is_admin === true);
      } else {
        // Fallback to fetching from database
        const adminStatus = await isCurrentUserAdmin();
        setIsAdmin(adminStatus);
      }
    } catch (err) {
      console.error('[useAdmin] Error checking admin status:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAdminStatus();
  }, [isAuthenticated, profile?.id, profile?.is_admin]);

  return {
    isAdmin,
    loading,
    error,
    refetch: checkAdminStatus,
  };
}
